import { useEffect, useRef, useMemo } from 'react';
import { useAudio } from '@chainlit/react-client';
import { useTheme } from '@/components/ThemeProvider';
import { hslToHex } from '@/lib/utils';

interface Props {
  size?: number;
  isActive?: boolean;
}

/**
 * Modern AI-style audio visualizer for the Realtime Assistant button.
 * Features a morphing blob/orb animation similar to ChatGPT voice mode or Siri.
 */
export default function RealtimeAudioVisualizer({ size = 48, isActive = true }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { wavRecorder, isAiSpeaking } = useAudio();
  const { variant } = useTheme();
  const animationRef = useRef<number>();
  const timeRef = useRef(0);

  // Get theme-aware colors
  const colors = useMemo(() => {
    const root = document.documentElement;
    const styles = getComputedStyle(root);
    const primary = hslToHex(styles.getPropertyValue('--primary'));
    const foreground = hslToHex(styles.getPropertyValue('--foreground'));
    
    // AI-style gradient colors (cyan/purple/blue palette)
    return {
      primary,
      foreground,
      glow1: '#06b6d4', // cyan-500
      glow2: '#8b5cf6', // violet-500  
      glow3: '#3b82f6', // blue-500
      glow4: '#ec4899', // pink-500
    };
  }, [variant]);

  useEffect(() => {
    if (!isActive) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    canvas.width = size * dpr;
    canvas.height = size * dpr;
    canvas.style.width = `${size}px`;
    canvas.style.height = `${size}px`;
    ctx.scale(dpr, dpr);

    const centerX = size / 2;
    const centerY = size / 2;
    const baseRadius = size * 0.3;

    // Get audio amplitude
    const getAmplitude = () => {
      try {
        if (wavRecorder?.recording) {
          const freqData = wavRecorder.getFrequencies('voice');
          if (freqData?.values?.length) {
            const avg = freqData.values.reduce((a: number, b: number) => a + b, 0) / freqData.values.length;
            return Math.min(1, avg * 2);
          }
        }
      } catch {
        // Fallback to idle animation
      }
      return 0;
    };

    const render = () => {
      timeRef.current += 0.02;
      const t = timeRef.current;
      
      ctx.clearRect(0, 0, size, size);
      
      const amplitude = getAmplitude();
      const dynamicScale = 1 + amplitude * 0.4;
      
      // Create multiple layers for depth effect
      const layers = isAiSpeaking ? 5 : 4;
      
      for (let layer = layers - 1; layer >= 0; layer--) {
        const layerProgress = layer / layers;
        const layerRadius = baseRadius * dynamicScale * (0.6 + layerProgress * 0.5);
        const layerAlpha = isAiSpeaking 
          ? 0.15 + (1 - layerProgress) * 0.25
          : 0.1 + (1 - layerProgress) * 0.2;
        
        // Create morphing blob shape using noise-like functions
        ctx.beginPath();
        
        const points = 64;
        for (let i = 0; i <= points; i++) {
          const angle = (i / points) * Math.PI * 2;
          
          // Multiple sine waves for organic morphing
          const wave1 = Math.sin(angle * 3 + t * 2) * 0.15;
          const wave2 = Math.sin(angle * 5 - t * 1.5) * 0.1;
          const wave3 = Math.cos(angle * 2 + t * 3) * 0.08;
          const wave4 = Math.sin(angle * 7 + t * 0.8) * 0.05;
          
          // Audio-reactive wave
          const audioWave = amplitude * Math.sin(angle * 8 - t * 4) * 0.2;
          
          const noise = wave1 + wave2 + wave3 + wave4 + audioWave;
          const r = layerRadius * (1 + noise * (isAiSpeaking ? 1.5 : 1));
          
          const x = centerX + Math.cos(angle) * r;
          const y = centerY + Math.sin(angle) * r;
          
          if (i === 0) {
            ctx.moveTo(x, y);
          } else {
            ctx.lineTo(x, y);
          }
        }
        
        ctx.closePath();
        
        // Create gradient for each layer
        const gradient = ctx.createRadialGradient(
          centerX, centerY, 0,
          centerX, centerY, layerRadius * 1.5
        );
        
        // Rotating color palette based on time
        const colorShift = t * 0.3 + layer * 0.5;
        const c1 = colors.glow1;
        const c2 = colors.glow2;
        const c3 = colors.glow3;
        const c4 = colors.glow4;
        
        // Cycle through colors
        const colorIndex = Math.floor(colorShift) % 4;
        const colorLerp = colorShift % 1;
        const colorPalette = [c1, c2, c3, c4, c1];
        
        gradient.addColorStop(0, `${colorPalette[colorIndex]}${Math.floor(layerAlpha * 255).toString(16).padStart(2, '0')}`);
        gradient.addColorStop(0.5, `${colorPalette[colorIndex + 1]}${Math.floor(layerAlpha * 0.7 * 255).toString(16).padStart(2, '0')}`);
        gradient.addColorStop(1, 'transparent');
        
        ctx.fillStyle = gradient;
        ctx.fill();
      }
      
      // Add inner glow/core
      const coreGradient = ctx.createRadialGradient(
        centerX, centerY, 0,
        centerX, centerY, baseRadius * 0.6 * dynamicScale
      );
      
      coreGradient.addColorStop(0, isAiSpeaking ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.7)');
      coreGradient.addColorStop(0.3, isAiSpeaking ? 'rgba(6,182,212,0.6)' : 'rgba(6,182,212,0.4)');
      coreGradient.addColorStop(1, 'transparent');
      
      ctx.beginPath();
      ctx.arc(centerX, centerY, baseRadius * 0.5 * dynamicScale, 0, Math.PI * 2);
      ctx.fillStyle = coreGradient;
      ctx.fill();
      
      // Add outer glow ring when AI is speaking
      if (isAiSpeaking) {
        const ringRadius = baseRadius * dynamicScale * 1.2;
        const pulseRadius = ringRadius + Math.sin(t * 4) * 3;
        
        ctx.beginPath();
        ctx.arc(centerX, centerY, pulseRadius, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(6, 182, 212, ${0.3 + Math.sin(t * 3) * 0.2})`;
        ctx.lineWidth = 2;
        ctx.stroke();
      }
      
      animationRef.current = requestAnimationFrame(render);
    };

    render();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [size, isActive, wavRecorder, isAiSpeaking, colors]);

  return (
    <div 
      className="relative flex items-center justify-center"
      style={{ width: size, height: size }}
    >
      {/* Ambient glow background */}
      <div 
        className="absolute inset-0 rounded-full blur-xl opacity-50 animate-pulse"
        style={{
          background: `radial-gradient(circle, ${colors.glow1}40 0%, ${colors.glow2}20 50%, transparent 70%)`,
        }}
      />
      <canvas 
        ref={canvasRef}
        className="relative z-10"
      />
    </div>
  );
}
